import { cn } from '@ai-tutor/ui';
import { LESSON_TABS } from './LessonTabs';
import type { LessonTab } from '@/types/lesson';

interface LessonBottomTabsProps {
  activeTab: LessonTab;
  onTabChange: (tab: LessonTab) => void;
}

/**
 * The lesson's four tabs as a bottom bar - the compact (phone and tablet)
 * placement of `LessonTabs`.
 *
 * At the bottom because that is where a thumb is, and because four tabs fit a
 * 390px screen exactly as icon-over-label items, where the top bar clipped
 * "Appendix" and pushed the Progress menu off-screen. The sub-tabs (Text /
 * Podcast / ...) stay at the top of the content, next to what they switch.
 *
 * Padded by the home-indicator inset so the labels clear it.
 */
export function LessonBottomTabs({
  activeTab,
  onTabChange,
}: LessonBottomTabsProps) {
  return (
    <nav
      aria-label='Lesson sections'
      className='flex flex-shrink-0 border-t border-border bg-card pb-[env(safe-area-inset-bottom)]'
    >
      {LESSON_TABS.map(tab => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type='button'
            onClick={() => onTabChange(tab.id)}
            // Same contract as the top bar: the active tab is named, not only
            // coloured.
            aria-current={isActive ? 'true' : undefined}
            className={cn(
              'flex min-h-14 flex-1 flex-col items-center justify-center gap-1 text-xs font-medium transition-colors duration-200',
              isActive
                ? 'text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <span
              className={cn(
                'flex h-7 w-12 items-center justify-center rounded-full transition-colors duration-200',
                isActive && 'bg-secondary'
              )}
            >
              <Icon className='h-5 w-5' />
            </span>
            {tab.label}
          </button>
        );
      })}
    </nav>
  );
}
